import React, { useEffect, useState } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  Loader2,
  ArrowLeft,
  AlertTriangle,
  Printer,
  Download,
  Eye,
  ChevronDown,
  AlertCircle,
  Undo2,
  CreditCard,
  Copy,
  Receipt,
  FileText
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { SalesInvoiceForm } from './SalesInvoiceForm'
import { SalesInvoiceFormValues } from './schema'
import {
  getVoucher,
  createVoucher,
  updateVoucher,
  listVoucherTypes
} from '@/app/services/voucherService'
import { getParty } from '@/app/services/partyService'
import { listSites } from '@/app/services/siteService'
import { listItems } from '@/app/services/itemService'
import { listGodowns } from '@/app/services/inventoryService'
import { Item } from '@/app/types/item'
import {
  VoucherCreate,
  VoucherUpdate,
  VoucherResponse,
  VoucherItemCreate
} from '@/app/types/voucher'
import { pdf } from '@react-pdf/renderer'
import { SalesInvoicePDFTemplate } from '@/app/components/SalesInvoicePDFTemplate'
import { Badge, BadgeProps } from '@/components/ui/badge'
import { format } from 'date-fns'
import { getCompanyDetailsForPrint } from '@/app/config/branding'
import { getSimplifiedSalesInvoiceStatus } from '@/app/lib/statusUtils'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from '@/components/ui/dropdown-menu'
import { Party } from '@/app/types/party'
import {
  draftInvoiceService,
  DraftInvoice
} from '@/app/pages/offline-invoices/services/draftInvoiceService'
import { QuickPaymentModal } from '../components/QuickPaymentModal'
import { searchItemsAsync as searchItemsSQLite } from '@/lib/indexeddb/database'
import { useSQLiteSync } from '@/hooks/useSQLiteSync'
import { useAuthStore } from '@/app/store/authStore'
import { UnpaidInvoiceOverrideModal } from '../components/UnpaidInvoiceOverrideModal'
import { PaymentAllocationModal } from '../components/PaymentAllocationModal'
import {
  getSalesInvoicePrintData,
  generateSalesInvoiceHTML,
  generateSalesInvoicePDF,
  previewSalesInvoicePDF,
  generateSalesInvoicePDFViaWorker,
  previewSalesInvoicePDFViaWorker,
  previewSalesInvoicePDFTaxExclusive,
  generateSalesInvoicePDFTaxExclusive
} from '@/app/services/salesInvoicePrintService'
import { linkSalesOrderToInvoice, getSalesOrderByInvoice } from '@/app/services/documentLinkService'


// Interface to extend VoucherResponse with status properties that might be returned by API
interface ExtendedVoucherResponse extends VoucherResponse {
  status?: string
  payment_status?: string
}

// Import web API client for browser compatibility
import '@/app/api/setupWindowApi'

// Use window.api with proper typing
const api = window.api

const SalesInvoiceDetailPage: React.FC = (): JSX.Element => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const isEditMode = !!id
  const userOrgId = useAuthStore(state => state.userOrgId)

  // Check if we're coming from offline invoice conversion, duplication, or sales order
  const locationState = location.state as {
    draftInvoice?: DraftInvoice
    fromDraft?: boolean
    duplicateInvoice?: any
    fromDuplicate?: boolean
    fromSalesOrder?: boolean
    salesOrderId?: number
    salesOrderNumber?: string
  } | null
  const draftInvoice = locationState?.draftInvoice
  const isFromDraft = locationState?.fromDraft || false
  const duplicateInvoice = locationState?.duplicateInvoice
  const isFromDuplicate = locationState?.fromDuplicate || false
  const isFromSalesOrder = locationState?.fromSalesOrder || false
  const salesOrderId = locationState?.salesOrderId
  const salesOrderNumber = locationState?.salesOrderNumber

  // Initialize SQLite for local search
  const { isInitialized: isSQLiteReady } = useSQLiteSync()
  const localItemCount = 0 // SQLite sync doesn't provide item count

  // State for item search
  const [isSearchingItems, setIsSearchingItems] = useState(false)

  // State for PDF operations
  const [isPdfLoading, setIsPdfLoading] = useState(false)

  // State for quick payment modal
  const [isQuickPaymentModalOpen, setIsQuickPaymentModalOpen] = useState(false)
  
  // State for payment allocation modal
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false)

  // State for unpaid invoice override modal
  const [isUnpaidOverrideModalOpen, setIsUnpaidOverrideModalOpen] = useState(false)
  const [unpaidInvoiceData, setUnpaidInvoiceData] = useState<{
    errorType: 'credit_limit_exceeded' | 'unpaid_invoices_exist'
    message: string
    outstandingBalance: number
    creditLimit?: number
    creditLimitSource?: 'party_specific' | 'organization_default' | 'none'
    unpaidInvoices?: Array<{
      voucher_number: string
      date: string
      amount: number
      payment_status: string
    }>
    partyName: string
    partyId?: number
    overrideMethod?: 'pin' | 'otp'
    requiresOverride: boolean
  } | null>(null)
  const [pendingFormData, setPendingFormData] = useState<SalesInvoiceFormValues | null>(null)
  const [isUpdateMode, setIsUpdateMode] = useState(false)
  const [overridePinError, setOverridePinError] = useState<string | undefined>(undefined)
  
  // State to track if invoice was successfully created
  const [isInvoiceCreated, setIsInvoiceCreated] = useState(false)


  // Company details for PDF printing - from centralized branding config
  const companyDetails = getCompanyDetailsForPrint()

  // Function to search items for product selection in the grid
  const searchItems = async (query: string): Promise<Item[]> => {
    if (!query || query.length < 2) return []

    setIsSearchingItems(true)
    try {
      // Use local IndexedDB search if available and initialized
      if (isSQLiteReady && localItemCount > 0 && userOrgId) {
        console.log(`[IndexedDB] Searching locally among ${localItemCount} items`)
        const localResults = await searchItemsSQLite(query, userOrgId, 20)
        
        // Convert local items to match Item interface
        return localResults.map(item => ({
          id: item.id,
          name: item.name,
          code: item.code || undefined,
          hsn_code: item.hsn_code || undefined,
          description: item.description || undefined,
          unit: item.unit || undefined,
          tax_rate_id: item.tax_rate_id || undefined,
          category_id: item.category_id || undefined,
          is_active: true,
          organization_id: 0, // Will be set by backend
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }))
      } else {
        // Fall back to API search
        console.log('[API] Using API search (SQLite not ready or no local items)')
        // Add filter to only get active items
        const filters = JSON.stringify([{ field: 'is_active', value: true, op: 'eq' }])
        const result = await listItems({ search_query: query, limit: 20, filters })
        return result.items || []
      }
    } catch (error) {
      console.error('Error searching items:', error)
      // If SQLite fails, fall back to API
      if (isSQLiteReady) {
        try {
          // Add filter to only get active items
          const filters = JSON.stringify([{ field: 'is_active', value: true, op: 'eq' }])
          const result = await listItems({ search_query: query, limit: 20, filters })
          return result.items || []
        } catch (apiError) {
          console.error('API fallback also failed:', apiError)
          return []
        }
      }
      return []
    } finally {
      setIsSearchingItems(false)
    }
  }

  // Fetch voucher type ID for "Sales Invoice" (voucher_type_id = 1)
  const {
    data: voucherTypesData,
    isLoading: isLoadingVoucherTypes,
    isError: isErrorVoucherTypes,
    error: errorVoucherTypes
  } = useQuery({
    queryKey: ['voucher-types'],
    queryFn: () => listVoucherTypes({ limit: 100 })
  })

  // Find the Sales Invoice voucher type - using ID 1 to match the filter in list page
  const salesInvoiceVoucherType = voucherTypesData?.items?.find(
    (type) => type.id === 1 || type.code === 'SI'
  )

  // Fetch sales invoice if in edit mode
  const {
    data: salesInvoice,
    isLoading: isLoadingSalesInvoice,
    isError: isErrorSalesInvoice,
    error: errorSalesInvoice
  } = useQuery({
    queryKey: ['sales-invoice', id],
    queryFn: async (): Promise<ExtendedVoucherResponse | null> => {
      if (!id) return null
      const result = (await getVoucher(Number(id))) as ExtendedVoucherResponse
      if (result && !result.items) {
        if (Array.isArray(result.voucher_items) && result.voucher_items.length > 0) {
          result.items = result.voucher_items
        } else if (Array.isArray(result.document_items) && result.document_items.length > 0) {
          result.items = result.document_items
        }
      }
      return result
    },
    enabled: isEditMode
  })

  // Check if the invoice is in a finalized state (paid, partially paid, posted, or cancelled)
  const isInvoiceFinalized =
    salesInvoice?.is_cancelled ||
    salesInvoice?.status === 'Paid' ||
    salesInvoice?.status === 'Posted' ||
    salesInvoice?.payment_status === 'Paid' ||
    salesInvoice?.status === 'Partially Paid' ||
    salesInvoice?.payment_status === 'Partially Paid'

  // Update document title based on invoice
  useEffect(() => {
    if (salesInvoice?.voucher_number) {
      document.title = `Invoice #${salesInvoice.voucher_number}`
    } else if (isEditMode) {
      document.title = 'Loading Invoice...'
    } else {
      document.title = 'New Sales Invoice'
    }

    // Cleanup on unmount
    return () => {
      document.title = 'Clara Builders'
    }
  }, [salesInvoice?.voucher_number, isEditMode])

  // Fetch party details if we have a party_id from the sales invoice
  const partyId = salesInvoice?.party_id
  const { data: partyDetails, isLoading: isLoadingPartyDetails } = useQuery<Party | null>({
    queryKey: ['party-details', partyId],
    queryFn: () => (partyId ? getParty(partyId) : null),
    enabled: !!partyId
  })

  // Fetch linked sales order if exists
  const { data: linkedSalesOrder } = useQuery({
    queryKey: ['linked-sales-order', id],
    queryFn: () => id ? getSalesOrderByInvoice(Number(id)) : null,
    enabled: isEditMode && !!id
  })

  // Fetch parties, sites, godowns and other needed data
  const { data: sitesData = { items: [] }, isLoading: isLoadingSites } = useQuery({
    queryKey: ['sites'],
    queryFn: () => listSites({ limit: 100 })
  })

  // Fetch godowns (warehouses) for item selection
  const { data: godownsData = { items: [] }, isLoading: isLoadingGodowns } = useQuery({
    queryKey: ['godowns'],
    queryFn: () => listGodowns({ limit: 100 })
  })

  // Create sales invoice mutation
  const createMutation = useMutation({
    mutationFn: (data: VoucherCreate) => createVoucher(data),
    onSuccess: async (result) => {
      queryClient.invalidateQueries({ queryKey: ['sales-invoices'] })
      toast.success(`Sales invoice #${result.voucher_number} created successfully`)

      // Mark invoice as created
      setIsInvoiceCreated(true)

      // Clear any pending form data and close the unpaid override modal
      setPendingFormData(null)
      setIsUnpaidOverrideModalOpen(false)
      setOverridePinError(undefined)

      // If this was converted from a draft invoice, delete the draft
      if (isFromDraft && draftInvoice?.id) {
        try {
          await draftInvoiceService.deleteDraftInvoice(draftInvoice.id)
          toast.success('Draft invoice has been removed')
        } catch (error) {
          console.error('Failed to delete draft invoice:', error)
          toast.error('Created invoice but failed to remove draft')
        }
      }

      // If this was created from a sales order, link them
      if (isFromSalesOrder && salesOrderId && result.id) {
        try {
          await linkSalesOrderToInvoice(salesOrderId, result.id)
          toast.success(`Linked to Sales Order #${salesOrderNumber}`)
          // Invalidate sales order queries to update its status
          queryClient.invalidateQueries({ queryKey: ['sales-order', salesOrderId.toString()] })
          queryClient.invalidateQueries({ queryKey: ['linked-invoice', salesOrderId.toString()] })
        } catch (error) {
          console.error('Failed to link sales order to invoice:', error)
          toast.warning('Invoice created but failed to link to sales order')
        }
      }

      // Navigate to list page after successful creation
      navigate('/sales-invoices/list')
    },
    onError: (error: any) => {
      // Check if it's a credit limit or unpaid invoice validation error
      const errorDetail = error?.response?.data?.detail
      const errorType = errorDetail?.error
      
      if (error?.response?.status === 400 && (errorType === 'credit_limit_exceeded' || errorType === 'unpaid_invoices_exist')) {
        // Handle both new credit limit and old unpaid invoice errors
        setUnpaidInvoiceData({
          errorType: errorType,
          message: errorDetail.message || 'Outstanding balance exceeds limit',
          outstandingBalance: errorDetail.outstanding_balance || errorDetail.total_unpaid_amount || 0,
          creditLimit: errorDetail.credit_limit,
          creditLimitSource: errorDetail.credit_limit_source,
          unpaidInvoices: errorDetail.unpaid_invoices,
          partyName: errorDetail.party_name || 'Customer',
          partyId: pendingFormData?.party_id,
          overrideMethod: errorDetail.override_method || 'pin',
          requiresOverride: errorDetail.requires_override || errorDetail.requires_override_pin || false
        })
        setIsUpdateMode(false) // Set to false for create mode
        setIsUnpaidOverrideModalOpen(true)
      } else if (error?.response?.status === 400 && errorDetail) {
        const errorMessage = typeof errorDetail === 'string' 
          ? errorDetail 
          : errorDetail.message || errorDetail.error || 'Unknown error'
        
        if (errorMessage.includes('Invalid override PIN') || errorMessage.includes('Invalid OTP')) {
          // Invalid PIN/OTP error
          setOverridePinError(errorMessage.includes('Invalid OTP') ? 'Invalid OTP. Please try again.' : 'Invalid PIN. Please try again.')
          // Keep the modal open for retry
        } else if (errorMessage.includes('OTP has expired')) {
          setOverridePinError('OTP has expired. Please request a new one.')
          // Keep the modal open for retry
        } else if (errorMessage.includes('No OTP has been generated')) {
          setOverridePinError('Please request an OTP first.')
          // Keep the modal open for retry
        } else {
          toast.error(`Failed to create sales invoice: ${errorMessage}`)
          // Clear any pending form data on other errors
          setPendingFormData(null)
          setIsUpdateMode(false)
          setIsUnpaidOverrideModalOpen(false)
        }
      } else {
        toast.error(`Failed to create sales invoice: ${error?.response?.data?.detail || error.message}`)
        // Clear any pending form data on other errors
        setPendingFormData(null)
        setIsUpdateMode(false)
        setIsUnpaidOverrideModalOpen(false)
      }
    }
  })

  // Update sales invoice mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: VoucherUpdate & { override_pin?: string; override_otp?: string } }) => updateVoucher(id, data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['sales-invoices'] })
      queryClient.invalidateQueries({ queryKey: ['sales-invoice', id] })
      toast.success(`Sales invoice #${result.voucher_number} updated successfully`)
      // Clear any pending data on success
      setPendingFormData(null)
      setIsUpdateMode(false)
      setIsUnpaidOverrideModalOpen(false)
    },
    onError: (error: any) => {
      // Check if it's a credit limit or unpaid invoice validation error
      const errorDetail = error?.response?.data?.detail
      const errorType = errorDetail?.error

      if (error?.response?.status === 400 && (errorType === 'credit_limit_exceeded' || errorType === 'unpaid_invoices_exist')) {
        // Handle both new credit limit and old unpaid invoice errors
        setUnpaidInvoiceData({
          errorType: errorType,
          message: errorDetail.message || 'Outstanding balance exceeds limit',
          outstandingBalance: errorDetail.outstanding_balance || errorDetail.total_unpaid_amount || 0,
          creditLimit: errorDetail.credit_limit,
          creditLimitSource: errorDetail.credit_limit_source,
          unpaidInvoices: errorDetail.unpaid_invoices,
          partyName: errorDetail.party_name || 'Customer',
          partyId: pendingFormData?.party_id,
          overrideMethod: errorDetail.override_method || 'pin',
          requiresOverride: errorDetail.requires_override || errorDetail.requires_override_pin || false
        })
        setIsUpdateMode(true)
        setIsUnpaidOverrideModalOpen(true)
      } else if (error?.response?.status === 400 && errorDetail) {
        const errorMessage = typeof errorDetail === 'string'
          ? errorDetail
          : errorDetail.message || errorDetail.error || 'Unknown error'

        if (errorMessage.includes('Invalid override PIN') || errorMessage.includes('Invalid OTP')) {
          // Invalid PIN/OTP error
          setOverridePinError(errorMessage.includes('Invalid OTP') ? 'Invalid OTP. Please try again.' : 'Invalid PIN. Please try again.')
          // Keep the modal open for retry
        } else if (errorMessage.includes('OTP has expired')) {
          setOverridePinError('OTP has expired. Please request a new one.')
          // Keep the modal open for retry
        } else if (errorMessage.includes('No OTP has been generated')) {
          setOverridePinError('Please request an OTP first.')
          // Keep the modal open for retry
        } else {
          toast.error(`Failed to update sales invoice: ${errorMessage}`)
          // Clear any pending form data on other errors
          setPendingFormData(null)
          setIsUpdateMode(false)
          setIsUnpaidOverrideModalOpen(false)
        }
      } else {
        toast.error(`Failed to update sales invoice: ${error?.response?.data?.detail || error.message}`)
        // Clear any pending form data on other errors
        setPendingFormData(null)
        setIsUpdateMode(false)
        setIsUnpaidOverrideModalOpen(false)
      }
    }
  })


  // Handler for creating Credit Note
  const handleCreateCreditNote = () => {
    if (!id || !salesInvoice) return
    toast.info('Redirecting to create Credit Note...')
    navigate(`/credit-notes/new?fromInvoiceId=${id}`)
  }

  // Handle form submission
  const handleSubmit = (formData: SalesInvoiceFormValues, overrideValue?: string, isOtp?: boolean): void => {
    if (isInvoiceFinalized) {
      toast.error('Cannot update a finalized invoice (Paid, Partially Paid, Posted, or Cancelled)')
      return
    }

    // Store form data for potential retry with PIN
    setPendingFormData(formData)

    // Process items: remove calculated 'amount', ensure types
    const processedItems: VoucherItemCreate[] = formData.items.map(({ amount, ...item }) => ({
      ...item,
      quantity: Number(item.quantity || 0),
      rate: Number(item.rate || 0),
      discount_percent: Number(item.discount_percent || 0),
      unit_id: 1, // Default unit_id - most systems use 1 as the default unit
      description: item.description || undefined, // Convert null to undefined
      // Note: In a production system, you might want to fetch the actual unit_id from the item data
    }))

    try {
      if (isEditMode && id) {
        // Prepare payload for VoucherUpdate
        const updatePayload: VoucherUpdate = {
          party_id: formData.party_id,
          site_id: formData.site_id,
          transaction_date: format(formData.transaction_date, 'yyyy-MM-dd'),
          due_date: formData.due_date ? format(formData.due_date, 'yyyy-MM-dd') : null,
          reference_number: formData.reference_number,
          narration: formData.narration,
          total_amount: formData.total_amount || 0, // Backend might recalculate anyway
          // Include discount fields
          discount_type: formData.discount_type || null,
          discount_value: formData.discount_value || null,
          // Include delivery charge
          delivery_charge: formData.delivery_charge || null
        }

        // Add override PIN or OTP if provided for update mode
        if (overrideValue) {
          if (isOtp) {
            (updatePayload as any).override_otp = overrideValue
          } else {
            (updatePayload as any).override_pin = overrideValue
          }
        }

        // @ts-ignore - VoucherUpdate type may not include items, but backend accepts it
        updatePayload.items = processedItems
        updateMutation.mutate({ id: Number(id), data: updatePayload })
      } else {
        // Prepare payload for VoucherCreate
        const createPayload: VoucherCreate & { override_pin?: string; override_otp?: string } = {
          voucher_type_id: formData.voucher_type_id,
          party_id: formData.party_id,
          site_id: formData.site_id,
          transaction_date: format(formData.transaction_date, 'yyyy-MM-dd'),
          due_date: formData.due_date ? format(formData.due_date, 'yyyy-MM-dd') : null,
          reference_number: formData.reference_number,
          narration: formData.narration,
          total_amount: formData.total_amount || 0,
          is_cancelled: false, // Default value for creation
          // Include discount fields
          discount_type: formData.discount_type || null,
          discount_value: formData.discount_value || null,
          // Include delivery charge
          delivery_charge: formData.delivery_charge || null
        }
        
        // Add override PIN or OTP if provided
        if (overrideValue) {
          if (isOtp) {
            createPayload.override_otp = overrideValue
          } else {
            createPayload.override_pin = overrideValue
          }
        }
        
        // @ts-ignore - VoucherCreate type may not include items, but backend accepts it
        createPayload.items = processedItems
        createMutation.mutate(createPayload)
      }
    } catch (error) {
      console.error('Error during submission:', error)
      toast.error(`Submission error: ${(error as Error).message}`)
    }
  }

  // Show error if voucher type not found
  useEffect(() => {
    if (voucherTypesData && !salesInvoiceVoucherType) {
      toast.error('Sales Invoice voucher type not found. Please create it first.')
    }
  }, [voucherTypesData, salesInvoiceVoucherType])

  // Handle errors consistently
  useEffect(() => {
    if (isErrorSalesInvoice) {
      toast.error(`Error loading sales invoice: ${(errorSalesInvoice as Error).message}`)
    }
    if (isErrorVoucherTypes) {
      toast.error(`Error loading voucher types: ${(errorVoucherTypes as Error).message}`)
    }
  }, [isErrorSalesInvoice, errorSalesInvoice, isErrorVoucherTypes, errorVoucherTypes])

  // PDF Generation and Handling Functions

  // Save PDF - Browser implementation
  const handleSavePDF = async (): Promise<void> => {
    try {
      setIsPdfLoading(true)
      if (!salesInvoice) throw new Error('Invoice data not available')

      // Generate PDF blob
      const pdfDoc = (
        <SalesInvoicePDFTemplate
          salesInvoice={salesInvoice as any}
          partyDetails={partyDetails || undefined}
          companyDetails={companyDetails}
        />
      )
      const blob = await pdf(pdfDoc).toBlob()
      
      // Create download link
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `Invoice_${salesInvoice.voucher_number || 'draft'}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
      
      toast.success('PDF saved successfully')
    } catch (error) {
      console.error('Error saving PDF:', error)
      toast.error(`Failed to save PDF: ${(error as Error).message}`)
    } finally {
      setIsPdfLoading(false)
    }
  }

  // Print PDF - Browser implementation
  const handlePrintPDF = async (): Promise<void> => {
    try {
      setIsPdfLoading(true)
      if (!salesInvoice) throw new Error('Invoice data not available')

      // Generate PDF blob
      const pdfDoc = (
        <SalesInvoicePDFTemplate
          salesInvoice={salesInvoice as any}
          partyDetails={partyDetails || undefined}
          companyDetails={companyDetails}
        />
      )
      const blob = await pdf(pdfDoc).toBlob()
      
      // Open PDF in new window and trigger print
      const url = URL.createObjectURL(blob)
      const printWindow = window.open(url, '_blank')
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print()
          // Clean up after a delay to ensure print dialog has opened
          setTimeout(() => URL.revokeObjectURL(url), 1000)
        }
      } else {
        URL.revokeObjectURL(url)
        toast.error('Please allow pop-ups to print the PDF')
      }
    } catch (error) {
      console.error('Error printing PDF:', error)
      toast.error(`Failed to print PDF: ${(error as Error).message}`)
    } finally {
      setIsPdfLoading(false)
    }
  }

  // Preview PDF - Browser implementation
  const handlePreviewPDF = async (): Promise<void> => {
    try {
      setIsPdfLoading(true)
      if (!salesInvoice) throw new Error('Invoice data not available')

      // Generate PDF blob
      const pdfDoc = (
        <SalesInvoicePDFTemplate
          salesInvoice={salesInvoice as any}
          partyDetails={partyDetails || undefined}
          companyDetails={companyDetails}
        />
      )
      const blob = await pdf(pdfDoc).toBlob()
      
      // Open PDF in new tab for preview
      const url = URL.createObjectURL(blob)
      const previewWindow = window.open(url, '_blank')
      if (!previewWindow) {
        URL.revokeObjectURL(url)
        toast.error('Please allow pop-ups to preview the PDF')
      } else {
        // Clean up after a delay
        setTimeout(() => URL.revokeObjectURL(url), 60000) // 1 minute
      }
    } catch (error) {
      console.error('Error previewing PDF:', error)
      toast.error(`Failed to preview PDF: ${(error as Error).message}`)
    } finally {
      setIsPdfLoading(false)
    }
  }

  // Preview HTML - Browser implementation using HTML template from backend
  const handlePreviewHTML = async (): Promise<void> => {
    try {
      setIsPdfLoading(true)
      
      if (!salesInvoice || !id) throw new Error('No invoice data available')
      
      // Fetch print data from backend
      const printData = await getSalesInvoicePrintData(Number(id))
      
      // Generate HTML
      const htmlContent = generateSalesInvoiceHTML(printData, false)
      
      // Open in new window
      const printWindow = window.open('', '_blank')
      if (printWindow) {
        printWindow.document.write(htmlContent)
        printWindow.document.close()
      }
      
      toast.success('HTML preview opened in new tab')
    } catch (error) {
      console.error('HTML preview error:', error)
      toast.error(`Failed to preview: ${(error as Error).message}`)
    } finally {
      setIsPdfLoading(false)
    }
  }

  // Preview HTML as PDF
  const handlePreviewHTMLPDF = async (): Promise<void> => {
    try {
      setIsPdfLoading(true)
      
      if (!salesInvoice || !id) throw new Error('No invoice data available')
      
      // Fetch print data from backend
      const printData = await getSalesInvoicePrintData(Number(id))
      
      // Generate and preview PDF
      await previewSalesInvoicePDF(printData)
      
      toast.success('PDF preview opened in new tab')
    } catch (error) {
      console.error('PDF preview error:', error)
      toast.error(`Failed to preview PDF: ${(error as Error).message}`)
    } finally {
      setIsPdfLoading(false)
    }
  }

  // Save HTML as PDF
  const handleSaveHTMLPDF = async (): Promise<void> => {
    try {
      setIsPdfLoading(true)

      if (!salesInvoice || !id) throw new Error('No invoice data available')

      // Fetch print data from backend
      const printData = await getSalesInvoicePrintData(Number(id))

      // Generate and save PDF
      await generateSalesInvoicePDF(printData)

      toast.success('PDF saved successfully')
    } catch (error) {
      console.error('PDF save error:', error)
      toast.error(`Failed to save PDF: ${(error as Error).message}`)
    } finally {
      setIsPdfLoading(false)
    }
  }

  // Preview HTML as PDF - Tax Exclusive
  const handlePreviewHTMLPDFTaxExclusive = async (): Promise<void> => {
    try {
      setIsPdfLoading(true)

      if (!salesInvoice || !id) throw new Error('No invoice data available')

      // Fetch print data from backend
      const printData = await getSalesInvoicePrintData(Number(id))

      // Generate and preview tax-exclusive PDF
      await previewSalesInvoicePDFTaxExclusive(printData)

      toast.success('Tax-exclusive PDF preview opened in new tab')
    } catch (error) {
      console.error('Tax-exclusive PDF preview error:', error)
      toast.error(`Failed to preview tax-exclusive PDF: ${(error as Error).message}`)
    } finally {
      setIsPdfLoading(false)
    }
  }

  // Save HTML as PDF - Tax Exclusive
  const handleSaveHTMLPDFTaxExclusive = async (): Promise<void> => {
    try {
      setIsPdfLoading(true)

      if (!salesInvoice || !id) throw new Error('No invoice data available')

      // Fetch print data from backend
      const printData = await getSalesInvoicePrintData(Number(id))

      // Generate and save tax-exclusive PDF
      await generateSalesInvoicePDFTaxExclusive(printData)

      toast.success('Tax-exclusive PDF saved successfully')
    } catch (error) {
      console.error('Tax-exclusive PDF save error:', error)
      toast.error(`Failed to save tax-exclusive PDF: ${(error as Error).message}`)
    } finally {
      setIsPdfLoading(false)
    }
  }

  // Generate PDF via Cloudflare Worker (Server-side)
  const handleGenerateWorkerPDF = async (): Promise<void> => {
    try {
      setIsPdfLoading(true)

      if (!salesInvoice || !id) throw new Error('No invoice data available')

      // Fetch print data from backend
      const printData = await getSalesInvoicePrintData(Number(id))

      // Generate and save PDF via worker
      await generateSalesInvoicePDFViaWorker(printData)

      toast.success('PDF generated successfully via worker')
    } catch (error) {
      console.error('Worker PDF generation error:', error)
      toast.error(`Failed to generate PDF via worker: ${(error as Error).message}`)
    } finally {
      setIsPdfLoading(false)
    }
  }

  // Preview PDF generated by Cloudflare Worker
  const handlePreviewWorkerPDF = async (): Promise<void> => {
    try {
      setIsPdfLoading(true)

      if (!salesInvoice || !id) throw new Error('No invoice data available')

      // Fetch print data from backend
      const printData = await getSalesInvoicePrintData(Number(id))

      // Preview PDF via worker
      await previewSalesInvoicePDFViaWorker(printData)

      toast.success('PDF preview opened in new tab')
    } catch (error) {
      console.error('Worker PDF preview error:', error)
      toast.error(`Failed to preview PDF via worker: ${(error as Error).message}`)
    } finally {
      setIsPdfLoading(false)
    }
  }

  // Loading state
  const isLoading =
    isLoadingSalesInvoice ||
    isLoadingSites ||
    isLoadingVoucherTypes ||
    isLoadingGodowns ||
    isLoadingPartyDetails
  const isSubmitting = createMutation.isPending || updateMutation.isPending
  const hasError = isErrorSalesInvoice || isErrorVoucherTypes || !salesInvoiceVoucherType
  const canGeneratePdf = isEditMode && salesInvoice && !isLoading

  // Get status information for display
  const simplifiedStatus = getSimplifiedSalesInvoiceStatus(salesInvoice)

  const getDisplayBadgeVariant = (): BadgeProps['variant'] | null => {
    if (!salesInvoice) return null
    switch (simplifiedStatus) {
      case 'Paid':
        return 'success' // Success variant
      case 'Partially Paid':
        return 'warning' // Warning variant
      case 'Unpaid':
        return 'outline' // Outline variant
      case 'Cancelled':
        return 'destructive' // Destructive variant
      default:
        return 'secondary' // Secondary variant
    }
  }

  const statusBadgeVariant = getDisplayBadgeVariant()

  // Check if invoice can be marked as paid
  const canMarkAsPaid = salesInvoice && !salesInvoice.is_cancelled && simplifiedStatus !== 'Paid'

  // Handler for marking invoice as paid
  const handleMarkAsPaid = () => {
    if (salesInvoice) {
      setIsPaymentModalOpen(true)
    }
  }

  // Handler for duplicating invoice
  const handleDuplicateInvoice = () => {
    if (!salesInvoice) return
    
    // Prepare invoice data for duplication
    const duplicateData = {
      party_id: salesInvoice.party_id,
      site_id: salesInvoice.site_id,
      reference_number: '', // Clear reference number for new invoice
      narration: `Duplicated from Invoice #${salesInvoice.voucher_number}`,
      items: salesInvoice.items || [],
      // Reset dates to current
      transaction_date: new Date(),
      due_date: null,
      voucher_type_id: salesInvoice.voucher_type_id,
      total_amount: salesInvoice.total_amount
    }
    
    // Navigate to create page with the duplicate data
    navigate('/sales-invoices/new', { 
      state: { 
        duplicateInvoice: duplicateData,
        fromDuplicate: true 
      } 
    })
    toast.info('Invoice duplicated. Review and save the new invoice.')
  }

  // Handler for PIN/OTP override confirmation
  const handlePinOverrideConfirm = (value: string, isOtp?: boolean) => {
    if (!pendingFormData) {
      toast.error('No pending invoice data found')
      return
    }

    // Clear any previous error
    setOverridePinError(undefined)

    // Retry the submission with the PIN or OTP
    // Pass the override value to handleSubmit which will route to create or update
    handleSubmit(pendingFormData, value, isOtp)
  }

  // Define badge variant type explicitly
  type BadgeVariant = BadgeProps['variant']


  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-2xl font-bold">
            {isEditMode
              ? `Edit Sales Invoice ${salesInvoice?.voucher_number}`
              : 'Create Sales Invoice'}
          </h1>

          {/* Display invoice status */}
          {statusBadgeVariant && (
            <Badge 
              variant={statusBadgeVariant as BadgeVariant} 
              className={`ml-2 ${(simplifiedStatus === 'Partially Paid' || simplifiedStatus === 'Paid') ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}`}
              onClick={() => {
                if ((simplifiedStatus === 'Partially Paid' || simplifiedStatus === 'Paid') && salesInvoice) {
                  setIsPaymentModalOpen(true)
                }
              }}
            >
              {simplifiedStatus}
            </Badge>
          )}
          {/* Display linked sales order if exists */}
          {linkedSalesOrder && (
            <Badge 
              variant="outline" 
              className="ml-2 cursor-pointer hover:bg-accent"
              onClick={() => navigate(`/sales-orders/detail/${linkedSalesOrder.sales_order_id}`)}
            >
              <FileText className="h-3 w-3 mr-1" />
              From SO #{linkedSalesOrder.document_number}
            </Badge>
          )}
          {/* SQLite Status Indicator */}
          {isSQLiteReady && localItemCount > 0 && (
            <Badge variant="outline" className="ml-2 text-xs">
              Local Search ({localItemCount} items)
            </Badge>
          )}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* Mark as Paid Button */}
          {canMarkAsPaid && (
            <Button variant="default" size="sm" onClick={handleMarkAsPaid} disabled={isSubmitting}>
              <CreditCard className="h-4 w-4 mr-2" />
              Mark as Paid
            </Button>
          )}

          {/* Duplicate Button */}
          {isEditMode && salesInvoice && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleDuplicateInvoice}
              disabled={isSubmitting}
            >
              <Copy className="h-4 w-4 mr-2" />
              Duplicate
            </Button>
          )}

          {/* Create Credit Note Button */}
          {isEditMode && salesInvoice && !salesInvoice.is_cancelled && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleCreateCreditNote}
              disabled={isSubmitting}
            >
              <Undo2 className="h-4 w-4 mr-2" />
              Create Credit Note
            </Button>
          )}

          {/* PDF Action Dropdown */}
          {canGeneratePdf && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" disabled={isPdfLoading}>
                  {isPdfLoading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Printer className="h-4 w-4 mr-2" />
                  )}
                  Print Options
                  <ChevronDown className="h-4 w-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={handlePreviewHTML}
                  disabled={true}
                  className="opacity-50 cursor-not-allowed"
                >
                  <Receipt className="h-4 w-4 mr-2" />
                  Preview (HTML Format)
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={handlePreviewHTMLPDF}
                  disabled={isPdfLoading || !salesInvoice}
                >
                  <Receipt className="h-4 w-4 mr-2" />
                  Preview (HTML as PDF)
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={handleSaveHTMLPDF}
                  disabled={isPdfLoading || !salesInvoice}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Save (HTML as PDF)
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handlePreviewHTMLPDFTaxExclusive}
                  disabled={isPdfLoading || !salesInvoice}
                >
                  <Eye className="h-4 w-4 mr-2" />
                  Preview (HTML as PDF) - Tax Exclusive
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={handleSaveHTMLPDFTaxExclusive}
                  disabled={isPdfLoading || !salesInvoice}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Save (HTML as PDF) - Tax Exclusive
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handlePreviewWorkerPDF}
                  disabled
                >
                  <Eye className="h-4 w-4 mr-2" />
                  Preview (Worker PDF - High Quality)
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={handleGenerateWorkerPDF}
                  disabled
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download (Worker PDF - High Quality)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

        </div>
      </div>

      {isErrorSalesInvoice && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Error loading sales invoice: {(errorSalesInvoice as Error).message}
          </AlertDescription>
        </Alert>
      )}

      {isErrorVoucherTypes && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Error loading voucher types: {(errorVoucherTypes as Error).message}
          </AlertDescription>
        </Alert>
      )}

      {!salesInvoiceVoucherType && !isLoadingVoucherTypes && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Sales Invoice voucher type not found. Please create a voucher type with ID 1 and code
            &quot;SI&quot; first.
          </AlertDescription>
        </Alert>
      )}

      {godownsData.items.length === 0 && !isLoadingGodowns && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            No warehouses found. Please create at least one warehouse before creating sales
            invoices.
          </AlertDescription>
        </Alert>
      )}

      {/* Show warning if invoice is finalized */}
      {isInvoiceFinalized && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            This invoice has been{' '}
            {salesInvoice?.is_cancelled
              ? 'cancelled'
              : salesInvoice?.status === 'Posted'
                ? 'posted'
                : salesInvoice?.status === 'Partially Paid' || salesInvoice?.payment_status === 'Partially Paid'
                  ? 'partially paid'
                  : 'paid'}{' '}
            and cannot be modified.
          </AlertDescription>
        </Alert>
      )}

      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : !hasError && salesInvoiceVoucherType ? (
        <>
          <SalesInvoiceForm
            key={id || 'new'} // Force re-render when ID changes
            salesInvoice={salesInvoice ?? undefined}
            sites={sitesData.items}
            godowns={godownsData.items}
            voucherTypeId={salesInvoiceVoucherType.id}
            isSubmitting={isSubmitting}
            onSubmit={handleSubmit}
            searchItems={searchItems}
            isLoadingItems={isSearchingItems}
            isDisabled={isInvoiceFinalized || isInvoiceCreated}
            draftInvoice={draftInvoice}
            isFromDraft={isFromDraft}
            duplicateInvoice={duplicateInvoice}
            isFromDuplicate={isFromDuplicate}
            isInvoiceCreated={isInvoiceCreated}
          />

        </>
      ) : null}

      {/* Quick Payment Modal */}
      {salesInvoice && (
        <QuickPaymentModal
          isOpen={isQuickPaymentModalOpen}
          onClose={() => setIsQuickPaymentModalOpen(false)}
          invoice={salesInvoice}
        />
      )}

      {/* Unpaid Invoice Override Modal */}
      <UnpaidInvoiceOverrideModal
        isOpen={isUnpaidOverrideModalOpen}
        onClose={() => {
          setIsUnpaidOverrideModalOpen(false)
          setPendingFormData(null)
          setIsUpdateMode(false)
          setOverridePinError(undefined)
        }}
        onConfirm={handlePinOverrideConfirm}
        isLoading={isUpdateMode ? updateMutation.isPending : createMutation.isPending}
        errorType={unpaidInvoiceData?.errorType}
        message={unpaidInvoiceData?.message}
        outstandingBalance={unpaidInvoiceData?.outstandingBalance}
        creditLimit={unpaidInvoiceData?.creditLimit}
        creditLimitSource={unpaidInvoiceData?.creditLimitSource}
        unpaidInvoices={unpaidInvoiceData?.unpaidInvoices}
        partyName={unpaidInvoiceData?.partyName}
        partyId={unpaidInvoiceData?.partyId}
        error={overridePinError}
        overrideMethod={unpaidInvoiceData?.overrideMethod}
        newInvoiceAmount={pendingFormData?.total_amount}
      />

      {/* Payment Allocation Modal */}
      {salesInvoice && (
        <PaymentAllocationModal
          isOpen={isPaymentModalOpen}
          onClose={() => setIsPaymentModalOpen(false)}
          invoiceId={salesInvoice.id}
          invoiceNumber={salesInvoice.voucher_number}
          totalAmount={salesInvoice.total_amount}
          amountDue={salesInvoice.amount_due || 0}
        />
      )}

    </div>
  )
}

export default SalesInvoiceDetailPage
